import React from "react";
import {
	Delivery,
	DeliveryStatusEnum,
	DELIVERY_STATUS_LABELS,
} from "@/lib/types/delivery.types";
import { DeliveryStatusBadge } from "./delivery-status-badge.component";
import { useTeamProfilesContext } from "@/dentlab/src/context/single-entity-contexts/TeamProfilesContext";
import { getProfileDisplayName } from "@/lib/utils/client-utils";
import { Trash2, Image, Package } from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@/dentlab/src/components/ui/dropdown-menu";

interface DeliveryCardProps {
	delivery: Delivery;
	onEdit?: (delivery: Delivery) => void;
	onDelete: (deliveryId: number) => void;
	onStatusChange: (deliveryId: number, status: DeliveryStatusEnum) => void;
	onClick?: (delivery: Delivery) => void;
}

const statusBorderClasses: Record<DeliveryStatusEnum, string> = {
	[DeliveryStatusEnum.PENDING]: "border-l-gray-300",
	[DeliveryStatusEnum.IN_TRANSIT]: "border-l-yellow-400",
	[DeliveryStatusEnum.DELIVERED]: "border-l-green-500",
	[DeliveryStatusEnum.FAILED]: "border-l-red-500",
};

const statusDotClasses: Record<DeliveryStatusEnum, string> = {
	[DeliveryStatusEnum.PENDING]: "bg-gray-400",
	[DeliveryStatusEnum.IN_TRANSIT]: "bg-yellow-500",
	[DeliveryStatusEnum.DELIVERED]: "bg-green-500",
	[DeliveryStatusEnum.FAILED]: "bg-red-500",
};

const ALL_STATUSES = [
	DeliveryStatusEnum.PENDING,
	DeliveryStatusEnum.IN_TRANSIT,
	DeliveryStatusEnum.DELIVERED,
	DeliveryStatusEnum.FAILED,
];

export const DeliveryCard: React.FC<DeliveryCardProps> = ({
	delivery,
	onEdit,
	onDelete,
	onStatusChange,
	onClick,
}) => {
	const {
		teamProfiles: { teamProfilesLookup },
	} = useTeamProfilesContext();
	const courierName =
		delivery.courier_type === "person" && delivery.profile_id
			? getProfileDisplayName(
					teamProfilesLookup[delivery.profile_id],
					"Unbekannt"
				)
			: delivery.courier_company_name || "Unbekannt";

	const formatDate = (dateStr: string | null) => {
		if (!dateStr) return null;
		return new Date(dateStr).toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const hasFiles = delivery.file_ids && delivery.file_ids.length > 0;
	const availableStatuses = ALL_STATUSES.filter((s) => s !== delivery.status);

	return (
		<div
			className={`border border-gray-200 border-l-4 ${
				statusBorderClasses[delivery.status]
			} rounded-lg p-3 bg-white${
				onClick
					? " cursor-pointer hover:bg-gray-50 transition-colors"
					: ""
			}`}
			onClick={() => onClick?.(delivery)}
			data-testid={`delivery-card-${delivery.id}`}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex flex-col gap-1 min-w-0">
					<div className="flex items-center gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									className="cursor-pointer"
									onClick={(e) => e.stopPropagation()}
									aria-label="Status ändern"
									data-testid={`delivery-status-trigger-${delivery.id}`}
								>
									<DeliveryStatusBadge
										status={delivery.status}
										id={delivery.id}
										showChevron
									/>
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start">
								{availableStatuses.map((s) => (
									<DropdownMenuItem
										key={s}
										className="cursor-pointer"
										onClick={(e) => {
											e.stopPropagation();
											onStatusChange(delivery.id, s);
										}}
										data-testid={`delivery-status-option-${s}`}
									>
										<span
											className={`h-2 w-2 rounded-full shrink-0 ${statusDotClasses[s]}`}
										/>
										{DELIVERY_STATUS_LABELS[s]}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
						{hasFiles && (
							<span className="flex items-center gap-0.5 text-xs text-gray-400">
								<Image className="h-3 w-3" />
								{delivery.file_ids.length}
							</span>
						)}
					</div>
					<p className="text-xs text-gray-500">
						{delivery.courier_type === "person"
							? "Kurier"
							: "Versand"}
						: <span className="text-gray-700">{courierName}</span>
					</p>
					{delivery.description && (
						<p className="text-xs text-gray-600 line-clamp-1">
							{delivery.description}
						</p>
					)}
					{delivery.scheduled_departure_at && (
						<p className="text-xs text-gray-400">
							Geplant:{" "}
							{formatDate(delivery.scheduled_departure_at)}
						</p>
					)}
					{delivery.delivered_at && (
						<p className="text-xs text-green-600">
							Zugestellt: {formatDate(delivery.delivered_at)}
						</p>
					)}
					{delivery.is_final_delivery && (
						<span className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded w-fit">
							<Package className="h-2.5 w-2.5" />
							Endlieferung
						</span>
					)}
				</div>
				<div className="flex items-center gap-0.5 shrink-0">
					{onEdit && (
						<Button
							variant="ghost"
							size="sm"
							className="h-6 w-6 p-0"
							onClick={(e) => {
								e.stopPropagation();
								onEdit(delivery);
							}}
							aria-label="Lieferung bearbeiten"
							data-testid={`delivery-edit-button-${delivery.id}`}
						>
							<Trash2 className="h-3 w-3" />
						</Button>
					)}
					<Button
						variant="ghost"
						size="sm"
						className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
						onClick={(e) => {
							e.stopPropagation();
							onDelete(delivery.id);
						}}
						aria-label="Lieferung löschen"
					>
						<Trash2 className="h-3 w-3" />
					</Button>
				</div>
			</div>
		</div>
	);
};
